import { axiosClient } from '@/shared';
import { ApiResponse, ErrorResponse } from '@/shared/types';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { ProjectDtoV2Schema, ProjectDtoV2Type } from './post-project-v2';

// ===================================
// Upload Complete Request Schema
// ===================================

export const ContentCompleteInfoSchema = z.object({
  contentId: z.number(),
  skipAnalysis: z.boolean().optional().default(false),
});

export type ContentCompleteInfoType = z.infer<typeof ContentCompleteInfoSchema>;

export const ProjectUploadCompleteRequestSchema = z.object({
  contents: z.array(ContentCompleteInfoSchema),
});

export type ProjectUploadCompleteRequestType = z.infer<
  typeof ProjectUploadCompleteRequestSchema
>;

type ProjectUploadCompleteResponse = ApiResponse<ProjectDtoV2Type>;

// ===================================
// API Function
// ===================================

/**
 * v2.0 업로드 완료 API
 *
 * - Content 상태 ACTIVATE
 * - 포인트 차감
 * - AI 프로세스 시작
 *
 * @param projectId 프로젝트 ID
 * @param contentIds Content ID 배열 (skipAnalysis: false로 설정)
 */
export const patchProjectUploadCompleteApi = async ({
  projectId,
  contentIds,
}: {
  projectId: number;
  contentIds: number[];
}) => {
  try {
    // contentIds를 contents 형식으로 변환 (User 업로드는 skipAnalysis: false)
    const contents: ContentCompleteInfoType[] = contentIds.map((contentId) => ({
      contentId,
      skipAnalysis: false,
    }));

    const response = await axiosClient.patch<ProjectUploadCompleteResponse>(
      `/v2.0/project/${projectId}/upload-complete`,
      { contents },
    );
    return response.data;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const usePatchProjectUploadCompleteApi = () => {
  return useMutation<
    ProjectUploadCompleteResponse,
    AxiosError<ErrorResponse>,
    { projectId: number; contentIds: number[] }
  >({
    mutationFn: patchProjectUploadCompleteApi,
  });
};

// ===================================
// Upload Fail API (S3 업로드 실패 시)
// ===================================

type UploadFailResponse = ApiResponse<null>;

/**
 * v2.0 업로드 실패 API
 *
 * FE에서 S3 업로드 실패 시 BE에 알려 즉시 에러 처리
 * - PREPARE_CONTENTS → ERROR 상태 전이
 * - 포인트 즉시 롤백
 *
 * @param projectId 프로젝트 ID
 */
export const patchProjectUploadFailApi = async ({
  projectId,
  errorReason,
}: {
  projectId: number;
  errorReason?: string;
}) => {
  const response = await axiosClient.patch<UploadFailResponse>(
    `/v2.0/project/${projectId}/upload-fail`,
    undefined,
    { params: errorReason ? { errorReason } : undefined },
  );
  return response.data;
};

// ===================================
// Thumbnail Complete API (YouTube URL용)
// ===================================

type ThumbnailCompleteResponse = ApiResponse<null>;

/**
 * v2.0 썸네일 업로드 완료 API
 *
 * YouTube URL 프로젝트에서 썸네일만 S3에 업로드 후 호출
 * - Content 상태 변경 없음 (YouTube 다운로드 완료 전까지 NOT_READY 유지)
 * - upload-complete API와 분리하여 의도 명확화
 *
 * @param projectId 프로젝트 ID
 * @param contentIds 썸네일 업로드 완료된 Content ID 배열
 */
export const patchProjectThumbnailCompleteApi = async ({
  projectId,
  contentIds,
}: {
  projectId: number;
  contentIds: number[];
}) => {
  try {
    const response = await axiosClient.patch<ThumbnailCompleteResponse>(
      `/v2.0/project/${projectId}/thumbnail-complete`,
      { contentIds },
    );
    return response.data;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const usePatchProjectThumbnailCompleteApi = () => {
  return useMutation<
    ThumbnailCompleteResponse,
    AxiosError<ErrorResponse>,
    { projectId: number; contentIds: number[] }
  >({
    mutationFn: patchProjectThumbnailCompleteApi,
  });
};
